import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Link,
} from "@react-pdf/renderer";
import { Inspection } from "../../../../__generated__/types-and-hooks";

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 12,
    fontFamily: "Helvetica",
    position: "relative",
  },
  watermark: {
    position: "absolute",
    top: "40%",
    left: "15%",
    fontSize: 80,
    color: "#e0e0e0",
    transform: "rotate(-45deg)",
    opacity: 0.15,
    zIndex: -1,
  },
  header: {
    marginBottom: 30,
    textAlign: "center",
    borderBottom: "1px solid #ccc",
    paddingBottom: 10,
  },
  title: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#333",
  },
  subtitle: {
    fontSize: 10,
    color: "#666",
  },
  link: {
    fontSize: 10,
    color: "#1a0dab",
    textDecoration: "underline",
  },
  section: {
    marginBottom: 24,
  },
  heading: {
    fontSize: 14,
    fontWeight: "bold",
    textTransform: "uppercase",
    marginBottom: 8,
    color: "#222",
  },
  row: {
    flexDirection: "row",
    marginBottom: 6,
  },
  label: {
    width: 130,
    fontWeight: "bold",
    color: "#333",
  },
  value: {
    flex: 1,
    color: "#555",
  },
  contactCard: {
    border: "1px solid #aaa",
    borderRadius: 4,
    padding: 8,
    marginVertical: 6,
    backgroundColor: "#f9f9f9",
  },
  lotCard: {
    border: "1px solid #bbb",
    borderRadius: 4,
    padding: 10,
    marginVertical: 8,
    backgroundColor: "#f4f4f4",
  },
  lotSubheading: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 4,
    color: "#333",
  },
  aqlRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  aqlLabel: {
    fontSize: 10,
    fontWeight: "bold",
    width: 80,
  },
  aqlValue: {
    fontSize: 10,
    color: "#555",
    flex: 1,
  },
  table: {
    display: "flex",
    width: "auto",
    borderStyle: "solid",
    borderWidth: 1,
    borderBottom: 0,
    borderColor: "#ccc",
    marginBottom: 0,
  },
  tableRow: {
    flexDirection: "row",
  },
  tableHeader: {
    backgroundColor: "#eee",
    fontWeight: "bold",
  },
  tableCell: {
    padding: 4,
    fontSize: 8,
    // borderRight: "1px solid #ccc",
    // borderBottom: "1px solid #ccc",
    flex: 1,
    textAlign: "center",
  },
  skuCell: {
    flex: 2,
    textAlign: "left",
    paddingLeft: 6,
  },
});

type Props = {
  inspection: Inspection;
};

export function InspectionPDF({ inspection }: Props) {
  const renderRow = (label: string, value?: string | number | null) =>
    value ? (
      <View style={styles.row}>
        <Text style={styles.label}>{label}:</Text>
        <Text style={styles.value}>{value}</Text>
      </View>
    ) : null;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* HEADER */}
        <View style={styles.header}>
          <Text style={styles.title}>
            QCSKU Inspection #{inspection.uniqueId.toUpperCase()}
          </Text>
          <Link src="https://www.qcsku.com" style={styles.link}>
            www.qcsku.com
          </Link>
        </View>
        {/* INSPECTION */}
        <View style={styles.section}>
          <Text style={styles.heading}>Inspection</Text>
          {renderRow("Service Type", inspection.serviceType)}
          {renderRow("Status", inspection.status)}
          {renderRow("Start Date", inspection.startDate)}
          {renderRow("End Date", inspection.endDate)}
          {renderRow("Estimated Service Days", inspection.serviceDays)}
          {renderRow(
            "Total Cost",
            `${inspection.costTotal} ${inspection.costCurrency}`
          )}
          {renderRow("Cost Memo", inspection.costMemo)}
        </View>

        {/* SUPPLIER */}
        {inspection.supplier && (
          <View style={styles.section}>
            <Text style={styles.heading}>Supplier</Text>
            {renderRow("Name", inspection.supplier.name)}
            {renderRow("Address Line 1", inspection.supplier.addressLine1)}
            {renderRow("Address Line 2", inspection.supplier.addressLine2)}
            {renderRow("City", inspection.supplier.city)}
            {renderRow("Postal Code", inspection.supplier.postalCode)}
            {renderRow("Country", inspection.supplier.countryCode)}
            {(inspection.supplier.contacts ?? []).map((contact, idx) => (
              <View style={styles.contactCard} key={idx}>
                {renderRow("Contact Name", contact.name)}
                {(contact.contactMethods ?? []).map((method, i) =>
                  renderRow(method.key, method.value)
                )}
                {renderRow("Address Line 1", contact.addressLine1)}
                {renderRow("Address Line 2", contact.addressLine2)}
                {renderRow("City", contact.city)}
                {renderRow("State", contact.state)}
                {renderRow("Postal Code", contact.postalCode)}
                {renderRow("Country", contact.countryCode)}
              </View>
            ))}
          </View>
        )}

        {/* AGENCY */}
        {inspection.agency && (
          <View style={styles.section}>
            <Text style={styles.heading}>Agency</Text>
            {renderRow("Name", inspection.agency.name)}
            {renderRow("Slug", inspection.agency.slug)}
            {renderRow("About", inspection.agency.about)}
            {(inspection.agency.contacts ?? []).map((contact, i) =>
              renderRow(contact.key, contact.value)
            )}
          </View>
        )}

        {/* FINAL REPORT */}
        <View style={styles.section}>
          <Text style={styles.heading}>Final Report</Text>
          {renderRow("Result", inspection.result)}
          {renderRow("Inspected by", inspection.inspectedBy)}
          {renderRow("Inspector Notes", inspection.inspectorNotes)}
        </View>

        {/* LOTS & AQL SUMMARY */}
        {inspection.inspectionLots?.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.heading}>Inspection Lots</Text>

            {/* Table Header */}
            <View style={[styles.table, styles.tableHeader]}>
              <View style={styles.tableRow}>
                <Text style={[styles.tableCell, styles.skuCell]}>SKU</Text>
                <Text style={styles.tableCell}>Lot Size</Text>
                <Text style={styles.tableCell}>Level</Text>
                <Text style={styles.tableCell}>Sample</Text>
                <Text style={styles.tableCell}>Code</Text>

                {/* <Text style={styles.tableCell}>Critical AQL</Text>
                <Text style={styles.tableCell}>Accept</Text>
                <Text style={styles.tableCell}>Reject</Text> */}
                <Text style={styles.tableCell}>Critical</Text>

                {/* <Text style={styles.tableCell}>Major AQL</Text>
                <Text style={styles.tableCell}>Accept</Text>
                <Text style={styles.tableCell}>Reject</Text> */}
                <Text style={styles.tableCell}>Major</Text>

                {/* <Text style={styles.tableCell}>Minor AQL</Text>
                <Text style={styles.tableCell}>Accept</Text>
                <Text style={styles.tableCell}>Reject</Text> */}
                <Text style={styles.tableCell}>Minor</Text>
              </View>
            </View>

            {/* Table Body */}
            {inspection.inspectionLots.map((lot) => (
              <View key={lot.id} style={styles.table}>
                <View style={styles.tableRow}>
                  <Text style={[styles.tableCell, styles.skuCell]}>
                    {lot.sku}
                  </Text>
                  <Text style={styles.tableCell}>{lot.lotSize}</Text>
                  <Text style={styles.tableCell}>{lot.inspectionLevel}</Text>
                  <Text style={styles.tableCell}>{lot.sampleSize}</Text>
                  <Text style={styles.tableCell}>{lot.codeLetter}</Text>

                  {/* Critical AQL */}
                  {/* <Text style={styles.tableCell}>
                    {lot.criticalAql?.aql ?? "-"}
                  </Text>
                  <Text style={styles.tableCell}>
                    {lot.criticalAql?.acceptanceNumber ?? "-"}
                  </Text>
                  <Text style={styles.tableCell}>
                    {lot.criticalAql?.rejectionNumber ?? "-"}
                  </Text> */}
                  <Text style={styles.tableCell}>
                    {lot.criticalDefects ?? "-"}
                  </Text>

                  {/* Major AQL */}
                  {/* <Text style={styles.tableCell}>
                    {lot.majorAql?.aql ?? "-"}
                  </Text>
                  <Text style={styles.tableCell}>
                    {lot.majorAql?.acceptanceNumber ?? "-"}
                  </Text>
                  <Text style={styles.tableCell}>
                    {lot.majorAql?.rejectionNumber ?? "-"}
                  </Text> */}
                  <Text style={styles.tableCell}>
                    {lot.majorDefects ?? "-"}
                  </Text>

                  {/* Minor AQL */}
                  {/* <Text style={styles.tableCell}>
                    {lot.minorAql?.aql ?? "-"}
                  </Text>
                  <Text style={styles.tableCell}>
                    {lot.minorAql?.acceptanceNumber ?? "-"}
                  </Text>
                  <Text style={styles.tableCell}>
                    {lot.minorAql?.rejectionNumber ?? "-"}
                  </Text> */}
                  <Text style={styles.tableCell}>
                    {lot.minorDefects ?? "-"}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        )}
      </Page>
    </Document>
  );
}
